import { Component } from '@theme/component';

/**
 * Tab switching for sections/product-tabs.liquid.
 *
 * Every panel is already in the DOM, server-rendered. This only moves the
 * `hidden` attribute around and keeps the ARIA state in sync, so switching tabs
 * never touches the network.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} [tabs]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLAnchorElement[]} [viewAllLinks]
 *
 * @extends {Component<Refs>}
 */
class ProductTabs extends Component {
  requiredRefs = ['tablist'];

  /**
   * Panels whose carousels have already been re-measured after being revealed.
   * @type {WeakSet<Element>}
   */
  #remeasured = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @returns {HTMLButtonElement[]} */
  get tabs() {
    return this.refs.tabs ?? [];
  }

  /** @returns {HTMLElement[]} */
  get panels() {
    return this.refs.panels ?? [];
  }

  /** @returns {HTMLAnchorElement[]} */
  get viewAllLinks() {
    return this.refs.viewAllLinks ?? [];
  }

  /**
   * The index of the open tab.
   * @returns {number}
   */
  get selectedIndex() {
    const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

    return index === -1 ? 0 : index;
  }

  /**
   * Opens a tab and hides the others.
   *
   * @param {number} index - The index of the tab to open.
   * @param {Object} [options]
   * @param {boolean} [options.focusTab=false] - Whether to move focus to the opened tab.
   */
  select(index, { focusTab = false } = {}) {
    const { tabs, panels, viewAllLinks } = this;
    if (!tabs.length) return;

    const selected = Math.min(Math.max(index, 0), tabs.length - 1);

    for (const [i, tab] of tabs.entries()) {
      const isSelected = i === selected;

      tab.setAttribute('aria-selected', String(isSelected));
      // Roving tabindex: only the open tab is in the tab order.
      tab.tabIndex = isSelected ? 0 : -1;
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== selected;
    }

    for (const [i, link] of viewAllLinks.entries()) {
      link.hidden = i !== selected;
    }

    if (focusTab) tabs[selected]?.focus();

    const panel = panels[selected];
    if (panel) this.#remeasureCarousels(panel);
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const tab = this.#tabFrom(event.target);
    if (!tab) return;

    this.select(this.tabs.indexOf(tab));
  };

  /**
   * The ARIA tabs keyboard pattern: Left/Right move between tabs and wrap,
   * Home/End jump to the first and last.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const tab = this.#tabFrom(event.target);
    if (!tab) return;

    const { tabs } = this;
    const last = tabs.length - 1;
    const current = tabs.indexOf(tab);

    /** @type {number} */
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focusTab: true });
  };

  /**
   * Resolves an event target to one of this component's tabs, so that clicks and
   * keystrokes inside a panel are ignored.
   *
   * @param {EventTarget | null} target
   * @returns {HTMLButtonElement | null}
   */
  #tabFrom(target) {
    if (!(target instanceof Element)) return null;

    const tab = target.closest('[role="tab"]');

    return tab instanceof HTMLButtonElement && this.tabs.includes(tab) ? tab : null;
  }

  /**
   * Horizon's slideshow-component measures its slides on connect. A carousel
   * that connects inside a `hidden` panel measures zero, concludes that only one
   * slide is visible, and then its own ResizeObserver refuses to re-measure when
   * the panel is revealed -- the observer only re-measures when it already
   * believed more than one slide was visible (assets/slideshow.js, the
   * `visibleSlidesAmount > 1` guard in #setupSlideshow). The result is that every
   * slide but the first keeps aria-hidden="true" after the reveal, which hides
   * the products from assistive technology and defeats the content-visibility
   * rules in base.css that key off aria-hidden.
   *
   * Re-inserting the element at the same position re-runs connectedCallback with
   * real dimensions. That is the only full re-measure the component exposes, and
   * it is cheap because it happens once per panel.
   *
   * @param {Element} panel
   */
  #remeasureCarousels(panel) {
    if (this.#remeasured.has(panel)) return;
    this.#remeasured.add(panel);

    // Only the panel's own carousels. The product cards nest their own
    // slideshows inside, and re-inserting the outer element already re-runs
    // those too.
    const carousels = [...panel.querySelectorAll('slideshow-component')].filter(
      (carousel) => carousel.parentElement?.closest('slideshow-component') == null
    );

    for (const carousel of carousels) {
      const parent = carousel.parentNode;
      if (!parent) continue;

      const anchor = carousel.nextSibling;

      parent.removeChild(carousel);
      parent.insertBefore(carousel, anchor);
    }
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabs);
}
